import React, {useCallback, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Text} from '../../../styles/text';
import {Layout} from '../../../styles/layout';
import {modalTextColor, primaryColor} from '../../../utils/variables';
import VariationPicker from '../VariationPicker';
import VirtualTilesGrid from '../VirtualTilesGrid';
import {
    formatMoney,
    getProductPrice,
    getStoredTilesShowImages,
    LARGE_CATALOG_THRESHOLD,
    setStoredTilesShowImages,
} from '../modeStorage';
import {useProductFilter} from '../useProductFilter';
import * as S from '../styles';
import {
    getProductPickerConfirmAddLabel,
    ProductPickResult,
    ProductPickerCategory,
    ProductPickerContext,
    ProductPickerItem,
    ProductPickerVariation,
} from '../types';
import {resolveProductDetails, willFetchProductDetails} from '../resolveProductDetails';

interface Props {
    products: ProductPickerItem[];
    categories?: ProductPickerCategory[];
    brands?: string[];
    context?: ProductPickerContext;
    disabled?: boolean;
    loading?: boolean;
    compact?: boolean;
    singlePick?: boolean;
    entityType?: 'goods' | 'service';
    onPick: (result: ProductPickResult) => void;
    fetchProductDetails?: (productId: number) => Promise<ProductPickerItem | null>;
    forceProductDetails?: boolean;
}

interface CartLine {
    key: string;
    product: ProductPickerItem;
    variation: ProductPickerVariation | null;
    quantity: number;
    unitPrice: number;
}

const lineKey = (productId: number, variationId?: number | null) =>
    `${productId}:${variationId || 0}`;

const TilesCatalogMode = ({
    products,
    categories: categoriesProp,
    context,
    disabled,
    loading,
    compact,
    singlePick,
    entityType = 'goods',
    onPick,
    fetchProductDetails,
    forceProductDetails,
}: Props) => {
    const itemLabel = entityType === 'service' ? 'услугу' : 'товар';
    const confirmAddLabel = getProductPickerConfirmAddLabel(context);
    const [open, setOpen] = useState(false);
    const [categoryId, setCategoryId] = useState<number>(0);
    const [cart, setCart] = useState<CartLine[]>([]);
    const [pendingProduct, setPendingProduct] = useState<ProductPickerItem | null>(null);
    const [pendingVariation, setPendingVariation] = useState<ProductPickerVariation | null>(null);
    const [resolvingProduct, setResolvingProduct] = useState<ProductPickerItem | null>(null);
    const [showImages, setShowImages] = useState(() => getStoredTilesShowImages());
    const resolvingRef = useRef(false);
    const {query, setQuery, results} = useProductFilter(products);

    const handleShowImagesToggle = () => {
        setShowImages((prev) => {
            const next = !prev;
            setStoredTilesShowImages(next);
            return next;
        });
    };

    const resolveInitialCategory = (cats: ProductPickerCategory[], total: number) => {
        if (total < LARGE_CATALOG_THRESHOLD) return 0;
        const firstReal = cats.find((c) => c.id > 0);
        return firstReal?.id || 0;
    };

    const categories = useMemo(() => {
        if (categoriesProp?.length) {
            return [
                {id: 0, name: 'Все', count: products.length},
                ...categoriesProp,
            ];
        }
        const map = new Map<number, ProductPickerCategory>();
        products.forEach((p) => {
            const id = Number(p.category_id) || 0;
            if (!id) return;
            const current = map.get(id);
            if (current) {
                current.count = (current.count || 0) + 1;
            } else {
                map.set(id, {id, name: p.category || `Категория #${id}`, count: 1});
            }
        });
        return [
            {id: 0, name: 'Все', count: products.length},
            ...Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru')),
        ];
    }, [categoriesProp, products]);

    const filtered = useMemo(() => {
        return results.filter((product) => {
            if (categoryId && Number(product.category_id) !== categoryId) return false;
            return true;
        });
    }, [results, categoryId]);

    const qtyByProductId = useMemo(() => {
        const map = new Map<number, number>();
        cart.forEach((line) => {
            map.set(line.product.id, (map.get(line.product.id) || 0) + line.quantity);
        });
        return map;
    }, [cart]);

    const cartTotalQty = useMemo(
        () => cart.reduce((sum, line) => sum + line.quantity, 0),
        [cart],
    );

    const cartTotalAmount = useMemo(
        () => cart.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0),
        [cart],
    );

    const addToCart = useCallback((product: ProductPickerItem, variation?: ProductPickerVariation | null) => {
        const unitPrice = Number(variation?.price ?? getProductPrice(product));
        const key = lineKey(product.id, variation?.id);

        setCart((prev) => {
            const existing = prev.find((line) => line.key === key);
            if (existing) {
                return prev.map((line) =>
                    line.key === key
                        ? {...line, quantity: line.quantity + 1}
                        : line,
                );
            }
            return [
                ...prev,
                {
                    key,
                    product,
                    variation: variation || null,
                    quantity: 1,
                    unitPrice,
                },
            ];
        });
    }, []);

    const commitOne = useCallback((product: ProductPickerItem, variation?: ProductPickerVariation | null) => {
        const unitPrice = Number(variation?.price ?? getProductPrice(product));
        onPick({
            product,
            variation: variation || null,
            quantity: 1,
            price: unitPrice,
        });
        setPendingProduct(null);
        setPendingVariation(null);
        setOpen(false);
        setQuery('');
        setCategoryId(0);
    }, [onPick, setQuery]);

    const setLineQty = (key: string, quantity: number) => {
        setCart((prev) => {
            if (quantity <= 0) {
                return prev.filter((line) => line.key !== key);
            }
            return prev.map((line) =>
                line.key === key ? {...line, quantity} : line,
            );
        });
    };

    const removeLine = (key: string) => {
        setCart((prev) => prev.filter((line) => line.key !== key));
    };

    const startPick = useCallback(async (product: ProductPickerItem) => {
        if (resolvingRef.current) return;
        resolvingRef.current = true;

        const fetchOptions = {force: forceProductDetails};
        const showLoading = willFetchProductDetails(product, fetchProductDetails, fetchOptions);
        if (showLoading) {
            setResolvingProduct(product);
        }

        try {
            const resolved = await resolveProductDetails(product, fetchProductDetails, fetchOptions);
            if (resolved.has_variation) {
                setPendingProduct(resolved);
                setPendingVariation(null);
                return;
            }
            if (singlePick) {
                commitOne(resolved, null);
                return;
            }
            addToCart(resolved, null);
        } finally {
            resolvingRef.current = false;
            setResolvingProduct(null);
        }
    }, [forceProductDetails, fetchProductDetails, singlePick, commitOne, addToCart]);

    const confirmCart = () => {
        if (cart.length === 0) return;
        cart.forEach((line) => {
            onPick({
                product: line.product,
                variation: line.variation,
                quantity: line.quantity,
                price: line.unitPrice * line.quantity,
            });
        });
        setCart([]);
        setPendingProduct(null);
        setPendingVariation(null);
        setOpen(false);
        setQuery('');
        setCategoryId(0);
    };

    const closePanel = () => {
        setOpen(false);
        setPendingProduct(null);
        setPendingVariation(null);
    };

    const openPanel = () => {
        setCategoryId(resolveInitialCategory(categories, products.length));
        setOpen(true);
    };

    const panel = open
        ? createPortal(
            <S.PanelOverlay $fullscreen onClick={() => !pendingProduct && closePanel()}>
                <S.TouchPanel onClick={(e) => e.stopPropagation()}>
                    <S.PanelHeader>
                        <Layout vertical between={2}>
                            <Text extent="M" weight="bold" color={primaryColor}>
                                {singlePick ? `Выбрать ${itemLabel}` : 'Каталог'}
                            </Text>
                            <Text extent="S" color={modalTextColor}>
                                {singlePick
                                    ? 'Нажмите на карточку, чтобы выбрать'
                                    : 'Нажимайте на карточки · количество справа'}
                            </Text>
                        </Layout>
                        <S.TouchPrimaryButton $secondary type="button" onClick={closePanel}>
                            Закрыть
                        </S.TouchPrimaryButton>
                    </S.PanelHeader>

                    <S.PanelBody>
                        {resolvingProduct ? (
                            <S.DetailsLoadingCard style={{boxShadow: 'none', minWidth: '100%'}}>
                                <S.DetailsLoadingSpinner />
                                <Text extent="S" weight="bold" color={primaryColor}>
                                    {resolvingProduct.has_variation
                                        ? 'Загрузка вариаций…'
                                        : 'Загрузка товара…'}
                                </Text>
                                <Text extent="S" color={modalTextColor}>
                                    {resolvingProduct.name}
                                </Text>
                            </S.DetailsLoadingCard>
                        ) : pendingProduct ? (
                            <VariationPicker
                                product={pendingProduct}
                                selectedId={pendingVariation?.id}
                                onSelect={setPendingVariation}
                                onCancel={() => {
                                    setPendingProduct(null);
                                    setPendingVariation(null);
                                }}
                                onConfirm={() => {
                                    if (!pendingVariation || !pendingProduct) return;
                                    if (singlePick) {
                                        commitOne(pendingProduct, pendingVariation);
                                        return;
                                    }
                                    addToCart(pendingProduct, pendingVariation);
                                    setPendingProduct(null);
                                    setPendingVariation(null);
                                }}
                                confirmDisabled={!pendingVariation}
                            />
                        ) : (
                            <>
                                <S.TouchSearchShell>
                                    <S.SearchInput
                                        value={query}
                                        disabled={disabled || loading}
                                        placeholder="Поиск товара"
                                        onChange={(e) => setQuery(e.target.value)}
                                        inputMode="search"
                                    />
                                </S.TouchSearchShell>

                                <S.CategoryChips>
                                    {categories.map((category) => (
                                        <S.CategoryChip
                                            key={category.id}
                                            type="button"
                                            $active={categoryId === category.id}
                                            onClick={() => setCategoryId(category.id)}
                                        >
                                            {category.name}
                                            {category.count != null ? ` (${category.count})` : ''}
                                        </S.CategoryChip>
                                    ))}
                                </S.CategoryChips>

                                <S.CatalogListToolbar style={{justifyContent: 'flex-end'}}>
                                    <S.CatalogShowImagesToggle
                                        type="button"
                                        $active={showImages}
                                        onClick={handleShowImagesToggle}
                                        title={showImages ? 'Скрыть фото' : 'Показать фото'}
                                    >
                                        {showImages ? 'С фото' : 'Без фото'}
                                    </S.CatalogShowImagesToggle>
                                </S.CatalogListToolbar>

                                <S.TouchWorkspace $singlePick={singlePick}>
                                    {loading && products.length === 0 ? (
                                        <S.SkeletonStack aria-hidden>
                                            <S.SkeletonRow />
                                            <S.SkeletonRow />
                                            <S.SkeletonRow />
                                        </S.SkeletonStack>
                                    ) : (
                                        <VirtualTilesGrid
                                            products={filtered}
                                            qtyByProductId={qtyByProductId}
                                            showImages={showImages}
                                            singlePick={singlePick}
                                            loading={loading}
                                            onPick={startPick}
                                        />
                                    )}

                                    {!singlePick && (
                                    <S.CartPanel>
                                        <S.CartHeader>
                                            <Text extent="S" weight="bold" color={modalTextColor}>
                                                Выбрано
                                            </Text>
                                            <S.Badge>{cartTotalQty}</S.Badge>
                                        </S.CartHeader>

                                        <S.CartList>
                                            {cart.length === 0 ? (
                                                <S.EmptyState>
                                                    Нажмите на карточку, чтобы добавить товар
                                                </S.EmptyState>
                                            ) : (
                                                cart.map((line) => (
                                                    <S.CartItem key={line.key}>
                                                        <S.CartItemTop>
                                                            <Layout vertical between={2} style={{minWidth: 0, flex: 1}}>
                                                                <Text extent="S" weight="bold" color={modalTextColor}>
                                                                    {line.product.name}
                                                                </Text>
                                                                <S.ResultMeta>
                                                                    {[
                                                                        line.variation?.display_name,
                                                                        line.product.part_number,
                                                                        formatMoney(line.unitPrice),
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(' · ')}
                                                                </S.ResultMeta>
                                                            </Layout>
                                                            <S.CartRemoveButton
                                                                type="button"
                                                                onClick={() => removeLine(line.key)}
                                                            >
                                                                Удалить
                                                            </S.CartRemoveButton>
                                                        </S.CartItemTop>

                                                        <S.QtyStepper>
                                                            <S.QtyStepButton
                                                                type="button"
                                                                aria-label="Уменьшить количество"
                                                                onClick={() => setLineQty(line.key, line.quantity - 1)}
                                                            >
                                                                −
                                                            </S.QtyStepButton>
                                                            <S.QtyValue>{line.quantity}</S.QtyValue>
                                                            <S.QtyStepButton
                                                                type="button"
                                                                aria-label="Увеличить количество"
                                                                onClick={() => setLineQty(line.key, line.quantity + 1)}
                                                            >
                                                                +
                                                            </S.QtyStepButton>
                                                        </S.QtyStepper>

                                                        <Layout hAlign="space-between" vAlign="center">
                                                            <S.ResultMeta>Сумма</S.ResultMeta>
                                                            <S.ResultPrice>
                                                                {formatMoney(line.unitPrice * line.quantity)}
                                                            </S.ResultPrice>
                                                        </Layout>
                                                    </S.CartItem>
                                                ))
                                            )}
                                        </S.CartList>
                                    </S.CartPanel>
                                    )}
                                </S.TouchWorkspace>
                            </>
                        )}
                    </S.PanelBody>

                    {!pendingProduct && !singlePick && (
                        <S.TouchFooter>
                            <Layout vertical between={2}>
                                <Text extent="S" color={modalTextColor}>
                                    Позиций: {cart.length} · Кол-во: {cartTotalQty}
                                </Text>
                                <Text extent="S" weight="bold" color={primaryColor}>
                                    {formatMoney(cartTotalAmount)}
                                </Text>
                            </Layout>
                            <Layout between={8}>
                                <S.TouchPrimaryButton
                                    $secondary
                                    type="button"
                                    disabled={cart.length === 0}
                                    onClick={() => setCart([])}
                                >
                                    Очистить
                                </S.TouchPrimaryButton>
                                <S.TouchPrimaryButton
                                    type="button"
                                    disabled={cart.length === 0}
                                    onClick={confirmCart}
                                >
                                    {confirmAddLabel}
                                </S.TouchPrimaryButton>
                            </Layout>
                        </S.TouchFooter>
                    )}
                </S.TouchPanel>
            </S.PanelOverlay>,
            document.body,
        )
        : null;

    return (
        <S.Root $compact={compact}>
            <S.TriggerButton
                type="button"
                $compact={compact}
                disabled={disabled || loading}
                onClick={openPanel}
            >
                {singlePick ? `Выбрать ${itemLabel}` : 'Открыть каталог'}
            </S.TriggerButton>
            <S.Hint $compact={compact}>
                {singlePick
                    ? 'Плитки: выбор одной позиции'
                    : 'Плитки + корзина для сенсорного экрана'}
            </S.Hint>
            {panel}
        </S.Root>
    );
};

export default TilesCatalogMode;
